import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const type = searchParams.get("type") || "signup";
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await axios.post(`${API}/checkout/complete`, { session_id: sessionId }, { withCredentials: true });
        if (data.completed || data.payment_status === "paid") {
          setStatus("success");
          await checkAuth();
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000);
        else setStatus("error");
      }
    };
    poll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <><Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-6" />
          <h1 className="font-outfit text-2xl font-bold text-[#F5F5F5] mb-2">Processing Payment...</h1>
          <p className="text-[#A0A0A0] font-manrope">Please wait while we confirm your payment.</p></>
        )}
        {status === "success" && (
          <><div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-12 h-12 text-emerald-400" /></div>
          <h1 className="font-outfit text-3xl font-bold text-[#F5F5F5] mb-3" data-testid="payment-success-title">
            {type === "reconnection" ? "Service Restored!" : "Welcome to Prestigious Wireless!"}
          </h1>
          <p className="text-[#A0A0A0] font-manrope mb-8">
            {type === "reconnection" ? "Your service has been reactivated successfully." : "Your subscription is active and your SIM card is on the way!"}
          </p>
          <Link to="/dashboard" className="inline-block bg-[#D4AF37] hover:bg-[#F0C541] text-black px-8 py-3.5 rounded-full font-semibold transition-all" data-testid="go-to-dashboard">Go to Dashboard</Link></>
        )}
        {(status === "error" || status === "timeout") && (
          <><div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6"><XCircle className="w-12 h-12 text-red-400" /></div>
          <h1 className="font-outfit text-2xl font-bold text-[#F5F5F5] mb-3">Payment Issue</h1>
          <p className="text-[#A0A0A0] font-manrope mb-8">We couldn't confirm your payment. Please try again or contact support.</p>
          <Link to="/dashboard" className="inline-block border border-[#333] hover:border-[#D4AF37] text-[#F5F5F5] px-8 py-3.5 rounded-full font-semibold transition-all" data-testid="return-dashboard">Return to Dashboard</Link></>
        )}
      </div>
    </div>
  );
}
